"use client";

import React, { useState } from "react";
import { ArrowUpRight, Disc3, Funnel } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function TrackCard({
  id,
  title,
  excerpt,
  categories,
  link
}: {
  id: string;
  title: string;
  excerpt: string;
  categories: any[];
  link: string;
}) {
  const sanitizeExcerpt = (html: string) => {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(html, "text/html");
    const text = doc.body.textContent || "";
    return text.replace(/<[^>]*>/g, "");
  };
  return <div className="group flex flex-col border-2 border-border bg-secondary transition-colors hover:border-primary rounded-lg">
      <div className="flex flex-1 flex-col">
        <div className="border-b-2 border-border bg-background p-6 group-hover:border-primary transition-colors rounded-t-lg">
          <div className="mb-6">
            <h3 className="font-default text-2xl md:text-3xl font-bold uppercase tracking-tighter text-foreground break-words max-w-full">
              {title}
            </h3>
            {categories && Array.isArray(categories) && categories.length > 0 && <div className="flex flex-wrap gap-2 mt-4 mb-2">
                {categories.map((cat: any, i: number) => <span key={cat.id} data-index={i} className="bg-primary/10 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-primary rounded-lg">
                    {cat.name}
                  </span>)}
              </div>}
          </div>
          
          <div className="flex items-center justify-end">
            <Button asChild variant="outline" size="icon" className="shrink-0 rounded-lg">
              <Link to={link || "#"} aria-label={`View details for ${title}`}>
                <ArrowUpRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>

        <div className="flex-1 p-6 flex flex-col justify-between">
          <div className="font-mono text-sm text-muted-foreground mb-6">
            <p className="line-clamp-4">
              {sanitizeExcerpt(excerpt)}
            </p>
          </div>
          
          <Button asChild variant="link" className="h-auto p-0 font-bold uppercase text-primary hover:no-underline justify-start">
            <Link to={link || "#"}>
              Read More
            </Link>
          </Button>
        </div>
      </div>
    </div>;
}
function TrackListConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const filterOptions = [{
    label: 'All Works',
    category: ''
  }, {
    label: 'Mix Engineer',
    category: 'Mix Engineer'
  }, {
    label: 'Producer',
    category: 'Producer'
  }, {
    label: 'Songwriting',
    category: 'Songwriting'
  }];
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="h-96 animate-pulse border-2 border-border bg-secondary/50 rounded-lg" />)}
      </div>;
  }
  if (error) {
    return <div className="border-2 border-destructive bg-destructive/10 p-6 text-destructive rounded-lg">
        <p className="font-mono font-bold uppercase">Error loading tracks</p>
        <p className="text-sm">{error}</p>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="border-2 border-border bg-secondary p-12 text-center rounded-lg">
        <Disc3 className="mx-auto mb-4 size-12 text-muted-foreground opacity-50" />
        <p className="font-mono text-lg uppercase text-muted-foreground">
          No tracks found in the archive.
        </p>
      </div>;
  }
  const filteredPosts = selectedCategory === '' ? posts : posts.filter(post => post.categories?.some((cat: any) => String(cat.id) === selectedCategory || cat.name === selectedCategory));
  return <>
      <div className="mb-12 flex flex-col gap-6">
        <div className="flex items-center gap-2 font-mono text-sm font-bold uppercase tracking-wider text-muted-foreground">
          <Funnel className="size-4" />
          <span>Filter by Category:</span>
        </div>
        <div className="flex flex-wrap gap-3">
          {filterOptions.map((option, i) => <Button key={option.label} data-index={i} onClick={() => setSelectedCategory(option.category)} variant={selectedCategory === option.category ? "default" : "outline"} className={`rounded-lg font-mono text-xs uppercase tracking-widest transition-all ${selectedCategory === option.category ? "bg-primary text-background border-primary" : "bg-secondary border-2 border-border text-foreground hover:border-primary"}`}>
              {option.label}
            </Button>)}
        </div>
      </div>

      {filteredPosts.length === 0 ? <div className="border-2 border-border bg-secondary p-12 text-center rounded-lg">
          <Disc3 className="mx-auto mb-4 size-12 text-muted-foreground opacity-50" />
          <p className="font-mono text-lg uppercase text-muted-foreground">
            No tracks found in selected category.
          </p>
        </div> : <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {filteredPosts.map((post, i) => <TrackCard key={post.id} id={post.id} title={post.title} excerpt={post.excerpt || ""} categories={post.categories || []} link={post.link || "#"} />)}
        </div>}
    </>;
}
export default function TrackProductionShowcase() {
  const wp_query_params: any = {
    post_type: "post",
    per_page: 100
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="trackproductionshowcase" className="bg-background py-24 text-foreground">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-16 max-w-3xl">
          <h2 className="font-default text-4xl font-bold uppercase tracking-tighter md:text-6xl">
            <span className="text-foreground">THE</span>{" "}
            <span className="text-primary">SOUND</span>
          </h2>
          <p className="mt-4 font-mono text-lg text-muted-foreground">
            Explore selected works, raw recordings, and final mixes. Expand
            details to view production notes and technical approaches.
          </p>
        </div>

        <PostsProvider wp_query={wp_query}>
          <TrackListConsumer />
        </PostsProvider>
      </div>
    </section>;
}